import React from 'react';
import { 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  XCircle, 
  Calendar, 
  Edit, 
  FileText,
  Briefcase,
  Trash2
} from 'lucide-react';
import type { Matter } from '../../types';

interface MatterListProps {
  matters: Matter[];
  isLoading?: boolean;
  onMatterClick?: (matter: Matter) => void;
  onEditMatter?: (matter: Matter) => void;
  onDeleteMatter?: (matter: Matter) => void;
  className?: string;
}

const MatterList: React.FC<MatterListProps> = ({
  matters,
  isLoading = false,
  onMatterClick,
  onEditMatter,
  onDeleteMatter,
  className = ''
}) => {
  // Get status badge styling and icon
  const getStatusBadge = (status: Matter['status']) => {
    switch (status) {
      case 'active':
        return {
          className: 'bg-green-900 text-green-300 border border-green-700',
          icon: <Clock className="w-3 h-3" />,
          label: 'Active'
        };
      case 'completed':
        return {
          className: 'bg-blue-900 text-blue-300 border border-blue-700',
          icon: <CheckCircle className="w-3 h-3" />,
          label: 'Completed'
        };
      case 'on-hold':
        return {
          className: 'bg-yellow-900 text-yellow-300 border border-yellow-700',
          icon: <AlertCircle className="w-3 h-3" />,
          label: 'On Hold'
        };
      case 'cancelled':
        return {
          className: 'bg-red-900 text-red-300 border border-red-700',
          icon: <XCircle className="w-3 h-3" />,
          label: 'Cancelled'
        };
      default:
        return {
          className: 'bg-gray-700 text-gray-300 border border-gray-600',
          icon: <Clock className="w-3 h-3" />,
          label: 'Unknown'
        };
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Format date
  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Handle matter click
  const handleMatterClick = (matter: Matter) => {
    if (onMatterClick) {
      onMatterClick(matter);
    }
  };

  // Handle edit click
  const handleEditClick = (e: React.MouseEvent, matter: Matter) => {
    e.stopPropagation(); // Prevent matter click
    if (onEditMatter) {
      onEditMatter(matter);
    }
  };

  // Handle delete click
  const handleDeleteClick = (e: React.MouseEvent, matter: Matter) => {
    e.stopPropagation(); // Prevent matter click
    if (onDeleteMatter) {
      onDeleteMatter(matter);
    }
  };

  if (isLoading) {
    return (
      <div className={`space-y-4 ${className}`}>
        {Array.from({ length: 3 }).map((_, index) => (
          <div
            key={index}
            className="bg-gray-800 border border-gray-700 rounded-lg p-6 animate-pulse"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-3">
                <div className="h-5 bg-gray-700 rounded w-3/4"></div>
                <div className="h-4 bg-gray-700 rounded w-1/2"></div>
                <div className="h-4 bg-gray-700 rounded w-1/3"></div>
              </div>
              <div className="h-6 w-20 bg-gray-700 rounded"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (matters.length === 0) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <Briefcase className="w-12 h-12 text-gray-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-300 mb-2">No matters found</h3>
        <p className="text-gray-500">
          This client doesn't have any matters yet. Create a new matter to get started.
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {matters.map((matter) => {
        const statusBadge = getStatusBadge(matter.status);
        
        return (
          <div
            key={matter.id}
            onClick={() => handleMatterClick(matter)}
            className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:bg-gray-750 hover:border-gray-600 transition-all duration-200 cursor-pointer group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1 min-w-0">
                {/* Matter Title and Type */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-white group-hover:text-blue-300 transition-colors truncate">
                      {matter.title}
                    </h3>
                    <div className="flex items-center mt-1 space-x-2">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-900 text-blue-300 border border-blue-700">
                        <FileText className="w-3 h-3 mr-1" />
                        {matter.type}
                      </span>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex items-center space-x-2 ml-4">
                    {/* Edit Button */}
                    {onEditMatter && (
                      <button
                        onClick={(e) => handleEditClick(e, matter)}
                        className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                        title="Edit matter"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                    )}
                    
                    {/* Delete Button */}
                    {onDeleteMatter && (
                      <button
                        onClick={(e) => handleDeleteClick(e, matter)}
                        className="p-1.5 text-gray-400 hover:text-red-400 hover:bg-red-900 rounded-md transition-colors opacity-0 group-hover:opacity-100"
                        title="Delete matter"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Matter Description */}
                {matter.description && (
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {matter.description}
                  </p>
                )}

                {/* Matter Metadata */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center space-x-4">
                    <div className="flex items-center">
                      <Calendar className="w-4 h-4 mr-1" />
                      <span>Created {formatDate(matter.dateCreated)}</span>
                    </div>
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1" />
                      <span>Updated {formatDate(matter.lastActivity)}</span>
                    </div>
                  </div>
                  
                  {/* Status Badge and Billed Amount */}
                  <div className="flex items-center space-x-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadge.className}`}>
                      {statusBadge.icon}
                      <span className="ml-1">{statusBadge.label}</span>
                    </span>
                    <span className="text-green-400 font-medium">{formatCurrency(matter.billedAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default MatterList;
